import type { ComponentType } from 'react';
import type { PluginClientConfig } from '@vtak/shared';
import type { NavItem } from '@/constants/navigation';
import { resolveIcon } from '@/constants/icons';

/**
 * Registry mapping plugin:panel IDs to lazy-loaded React components.
 * Plugins register their panel components here as they are built.
 *
 * Key format: "pluginId:panelId"
 * Value: a function returning a dynamic import that resolves to a component
 */
const PLUGIN_PANEL_LOADERS: Record<string, () => Promise<{ default: ComponentType }>> = {
  'space:satellites': () => import('@vtak/mod-space/client').then(m => ({ default: m.SatellitePanel })),
  'space:conjunctions': () => import('@vtak/mod-space/client').then(m => ({ default: m.ConjunctionPanel })),
  'space:space-weather': () => import('@vtak/mod-space/client').then(m => ({ default: m.SpaceWeatherPanel })),
  'space:launch-schedule': () => import('@vtak/mod-space/client').then(m => ({ default: m.LaunchSchedulePanel })),
  'space:launch-pads': () => import('@vtak/mod-space/client').then(m => ({ default: m.LaunchPadsPanel })),
  'force-protect:patrols': () => import('@vtak/mod-force-protect/client').then(m => ({ default: m.PatrolPanel })),
  'force-protect:incidents': () => import('@vtak/mod-force-protect/client').then(m => ({ default: m.IncidentPanel })),
  'force-protect:fpcon': () => import('@vtak/mod-force-protect/client').then(m => ({ default: m.FpconPanel })),
  'patrol:patrol-map': () => import('@vtak/mod-patrol/client').then(m => ({ default: m.PatrolMapPanel })),
  'patrol:calls': () => import('@vtak/mod-patrol/client').then(m => ({ default: m.CallsPanel })),
  'incident:active': () => import('@vtak/mod-incident/client').then(m => ({ default: m.ActiveIncidentPanel })),
  'incident:ics': () => import('@vtak/mod-incident/client').then(m => ({ default: m.IcsPanel })),
  'cctv:cameras': () => import('@vtak/mod-cctv/client').then(m => ({ default: m.CctvPanel })),
  'transport:transports': () => import('@vtak/mod-transport/client').then(m => ({ default: m.TransportPanel })),
  'sim:targets': () => import('@vtak/mod-sim/client').then(m => ({ default: m.SimPanel })),
};

/**
 * Get a lazy-loaded panel component for a plugin panel.
 * Returns null if the panel is not registered.
 */
export function getPluginPanelLoader(
  pluginId: string,
  panelId: string,
): (() => Promise<{ default: ComponentType }>) | null {
  const key = `${pluginId}:${panelId}`;
  return PLUGIN_PANEL_LOADERS[key] ?? null;
}

/** Check if a panel ID is a plugin panel (contains ':') */
export function isPluginPanel(panelId: string): boolean {
  return panelId.includes(':');
}

/** Split a plugin panel ID into pluginId and panelId */
export function parsePluginPanelId(panelId: string): { pluginId: string; panelId: string } | null {
  const colonIndex = panelId.indexOf(':');
  if (colonIndex === -1) return null;
  return {
    pluginId: panelId.slice(0, colonIndex),
    panelId: panelId.slice(colonIndex + 1),
  };
}

/**
 * Build NavItem entries from active plugin panel definitions.
 * Each plugin panel becomes a sidebar icon button.
 */
export function buildPluginNavItems(plugins: PluginClientConfig[]): NavItem[] {
  const items: NavItem[] = [];

  for (const plugin of plugins) {
    for (const panel of plugin.panels) {
      items.push({
        id: `${plugin.id}:${panel.id}`,
        label: panel.label,
        icon: resolveIcon(panel.icon),
      });
    }
  }

  return items;
}
